/**
 * Hide and Seek Teams — Teams tab
 *
 * The two teams side by side: each team's Team Captain, its members (move them
 * to the other team or off it, disable or enable them) and how many are
 * online. Below, the players on no team yet; who signed up but hasn't joined
 * (no-shows on the night); who joined without a matching sign-up (walk-ins, or
 * a player whose Discord didn't link, matched to their sign-up here); and the
 * snake draft: its pick time and first pick, start and stop, and while it runs
 * whose turn it is, the time left and the players still to pick (the admin can
 * pick for the captain).
 *
 * Every player shows whether they signed up and whether they are online,
 * offline, not joined yet or disabled. Players from sign-ups who haven't
 * joined are on the teams from team creation, so the draft can run days ahead.
 *
 * Teams can be changed while the capability matrix allows (team creation and
 * the lobby) and no draft is running; the draft runs during team creation.
 * Disabling and enabling a player works at any time.
 */

import { useEffect, useMemo, useState } from 'react';
import { useSocketContext, useServerClockOffset } from '@swtorbit/shared-react-hooks';
import {
  GameType,
  HNST_DRAFT_PICK_SECONDS_DEFAULT,
  HNST_DRAFT_PICK_SECONDS_MAX,
  HNST_DRAFT_PICK_SECONDS_MIN,
  HNST_TEAMS,
  HNST_TEAM_KEYS,
  SocketEvent,
  canAdminDo,
  getCurrentPicker,
  getHnstTeamOf,
  getPlayerAvatarId,
  getPlayerDisplayName,
  otherHnstTeam,
  type Game,
  type HnstActionResponse,
  type HnstTeamKey,
  type Player,
} from '@swtorbit/shared-socket-events';
import { HnstTeamIcon, HnstTeamName, PlayerAvatar } from '@swtorbit/shared-components';
import { useGameContext } from '../../contexts/GameContext';
import { useHnstContext } from '../../contexts/HnstContext';
import { useRegistrations } from '../../hooks/useRegistrations';

/** Players still in the game. */
function inGame(game: Game): Player[] {
  return game.players.filter((p) => p.status !== 'left');
}

/** Players who can be made captain or picked: enabled and still in the game. */
function activePlayers(game: Game): Player[] {
  return inGame(game).filter((p) => p.enabled !== false);
}

/** Who a player is on the night, for the badges. */
interface PlayerFacts {
  signedUp: boolean;
  /** 'disabled' wins over the rest. */
  presence: 'disabled' | 'awaiting' | 'online' | 'offline';
}

function useSignedUp(game: Game): (player: Player | undefined) => boolean {
  const registrations = useRegistrations({ gameId: game.dbId, roomCode: game.roomCode, archived: game.archived });
  const signedUpIds = useMemo(() => new Set(registrations.map((r) => r.discordId)), [registrations]);
  return (player) => !!player && (!!player.awaitingJoin || (!!player.discord?.discordId && signedUpIds.has(player.discord.discordId)));
}

function factsOf(player: Player | undefined, signedUp: boolean): PlayerFacts {
  let presence: PlayerFacts['presence'] = 'offline';
  if (player?.enabled === false) presence = 'disabled';
  else if (player?.awaitingJoin) presence = 'awaiting';
  else if (player?.status === 'connected') presence = 'online';
  return { signedUp, presence };
}

const PRESENCE_BADGE: Record<PlayerFacts['presence'], { label: string; className: string }> = {
  online: { label: 'Online', className: 'badge badge--sm badge--green' },
  offline: { label: 'Offline', className: 'badge badge--sm badge--slate' },
  awaiting: { label: 'Not joined yet', className: 'badge badge--sm badge--amber' },
  disabled: { label: 'Disabled', className: 'badge badge--sm badge--red' },
};

function PlayerBadges({ facts }: { facts: PlayerFacts }) {
  const presence = PRESENCE_BADGE[facts.presence];
  return (
    <>
      <span className={`${presence.className} ml-2xs`}>{presence.label}</span>
      <span className={`badge badge--sm ${facts.signedUp ? 'badge--blue' : 'badge--aqua'} ml-2xs`}>
        {facts.signedUp ? 'Signed up' : 'Walk-in'}
      </span>
    </>
  );
}

/** A short note after a name in a dropdown or the draft pool. */
function factsNote(facts: PlayerFacts): string {
  if (facts.presence === 'awaiting') return ' · not joined yet';
  if (!facts.signedUp) return ' · walk-in';
  return '';
}

export function HnstTeamsTab() {
  const { selectedGame: game } = useGameContext();
  if (!game || game.gameType !== GameType.HIDE_AND_SEEK_TEAMS) return null;
  return <TeamsTab game={game} />;
}

function TeamsTab({ game }: { game: Game }) {
  const actions = useHnstContext();
  const { socket } = useSocketContext();
  const isSignedUp = useSignedUp(game);
  const [error, setError] = useState<string | null>(null);

  const draftRunning = game.hnstDraft?.status === 'in_progress';
  const canEdit = canAdminDo(GameType.HIDE_AND_SEEK_TEAMS, game.status, 'canEditTeamMembers') && !draftRunning;
  const canDraft = canAdminDo(GameType.HIDE_AND_SEEK_TEAMS, game.status, 'canConfigureDraft');
  const readOnly = !!game.archived;
  const playerOf = (playerId: string) => game.players.find((p) => p.playerId === playerId);
  const name = (playerId: string) => getPlayerDisplayName(playerId, game.players);
  const facts = (playerId: string) => {
    const player = playerOf(playerId);
    return factsOf(player, isSignedUp(player));
  };

  /** Run an action and show its error, if any. */
  const run = async (action: Promise<HnstActionResponse>) => {
    const res = await action;
    setError(res.success ? null : res.error ?? 'Something went wrong');
  };

  /** Disable or enable a player (works at any time; the server broadcasts the game). */
  const setEnabled = (playerId: string, enabled: boolean) => {
    socket?.emit(SocketEvent.ADMIN_TOGGLE_PLAYER, { roomCode: game.roomCode, playerId, enabled });
  };

  /** Link a joined player to a sign-up's Discord account: they take over its stand-in. */
  const matchToSignup = (playerId: string, standIn: Player) => {
    if (!standIn.discord) return;
    socket?.emit(SocketEvent.DISCORD_ADMIN_LINK_PLAYER, {
      roomCode: game.roomCode,
      playerId,
      discordId: standIn.discord.discordId,
      discordUsername: standIn.discord.discordUsername,
    });
  };

  const enableToggle = (playerId: string) => {
    if (readOnly) return null;
    const enabled = playerOf(playerId)?.enabled !== false;
    return (
      <button
        className={`btn btn--sm btn--outline ${enabled ? 'btn--amber' : 'btn--green'}`}
        onClick={() => setEnabled(playerId, !enabled)}
        title={enabled ? 'Leave them out of rounds (e.g. they had to go)' : 'Let them play again'}
      >
        {enabled ? 'Disable' : 'Enable'}
      </button>
    );
  };

  const unassigned = inGame(game).filter((p) => !getHnstTeamOf(game, p.playerId));
  const standIns = inGame(game).filter((p) => p.awaitingJoin);
  const unmatched = inGame(game).filter((p) => !p.awaitingJoin && !isSignedUp(p));

  return (
    <div className="d-flex flex-column gap-base text-white">
      <h2>Teams</h2>
      {!canEdit && (
        <p className="field__hint">
          {draftRunning
            ? 'The draft is running: teams change as the captains pick.'
            : 'Teams can be changed during team creation and in the lobby. Players can be disabled or enabled at any time.'}
        </p>
      )}
      {error && <div className="alert alert--red">{error}</div>}

      <div className="grid grid--1-2 gap-base">
        {HNST_TEAM_KEYS.map((key) => {
          const team = game.hnstTeams?.find((t) => t.key === key);
          const members = team?.memberIds ?? [];
          const online = members.filter((id) => facts(id).presence === 'online').length;
          return (
            <div key={key} className={`card card--sm card--bg-midnight card--border card--border-${HNST_TEAMS[key].hue}`}>
              <h3>
                <HnstTeamName team={key} />{' '}
                <span className="text-slate fs-sm">({online} online / {members.length})</span>
              </h3>

              <div className="field">
                <label htmlFor={`hnst-captain-${key}`}>Team Captain</label>
                <select
                  id={`hnst-captain-${key}`}
                  className="select"
                  disabled={!canEdit}
                  value={team?.captainPlayerId ?? ''}
                  onChange={(e) => run(actions.setCaptain(key, e.target.value || null))}
                >
                  <option value="">— None —</option>
                  {activePlayers(game).map((p) => (
                    <option key={p.playerId} value={p.playerId}>
                      {name(p.playerId)}{factsNote(facts(p.playerId))}
                      {getHnstTeamOf(game, p.playerId) && getHnstTeamOf(game, p.playerId) !== key ? ' (other team)' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div className="d-flex flex-column gap-2xs">
                {members.length === 0 && <p className="text-slate fs-sm">No players yet.</p>}
                {members.map((playerId) => (
                  <div key={playerId} className="d-flex align-items-center gap-xs">
                    <PlayerAvatar avatarId={getPlayerAvatarId(game.players, playerId)} tone={HNST_TEAMS[key].hue} />
                    <span className="flex-grow-1">
                      {name(playerId)}
                      {team?.captainPlayerId === playerId && <span className="badge badge--amber ml-2xs">★ Captain</span>}
                      <PlayerBadges facts={facts(playerId)} />
                    </span>
                    {canEdit && (
                      <>
                        <button className="btn btn--sm btn--outline btn--slate" onClick={() => run(actions.assignPlayer(playerId, otherHnstTeam(key)))}>
                          → <HnstTeamIcon team={otherHnstTeam(key)} />
                        </button>
                        <button className="btn btn--sm btn--outline btn--red" onClick={() => run(actions.assignPlayer(playerId, null))}>
                          Remove
                        </button>
                      </>
                    )}
                    {enableToggle(playerId)}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="card card--sm card--bg-midnight card--border card--border-navy">
        <h3>On no team ({unassigned.length})</h3>
        {unassigned.length === 0 ? (
          <p className="text-slate fs-sm">Every player is on a team.</p>
        ) : (
          <div className="d-flex flex-column gap-2xs">
            {unassigned.map((p) => (
              <div key={p.playerId} className="d-flex align-items-center gap-xs">
                <PlayerAvatar avatarId={p.avatarId} />
                <span className="flex-grow-1">
                  {name(p.playerId)}
                  <PlayerBadges facts={facts(p.playerId)} />
                </span>
                {canEdit && HNST_TEAM_KEYS.map((key) => (
                  <button
                    key={key}
                    className={`btn btn--sm btn--outline btn--${HNST_TEAMS[key].hue}`}
                    onClick={() => run(actions.assignPlayer(p.playerId, key))}
                  >
                    <HnstTeamIcon team={key} /> {HNST_TEAMS[key].name}
                  </button>
                ))}
                {enableToggle(p.playerId)}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card card--sm card--bg-midnight card--border card--border-navy">
        <h3>Signed up, not joined yet ({standIns.length})</h3>
        <p className="field__hint">
          Everyone signed up is a player from team creation, so they can be drafted before they join. When they join
          in the client and their Discord links, they take this place on their team. Disable anyone who doesn't turn up.
        </p>
        {standIns.length === 0 ? (
          <p className="text-slate fs-sm">Everyone signed up has joined.</p>
        ) : (
          <div className="d-flex flex-column gap-2xs">
            {standIns.map((p) => {
              const team = getHnstTeamOf(game, p.playerId);
              return (
                <div key={p.playerId} className="d-flex align-items-center gap-xs">
                  <span className="flex-grow-1">
                    {name(p.playerId)}
                    {team ? <> — <HnstTeamName team={team} /></> : <span className="text-slate"> — on no team</span>}
                    <PlayerBadges facts={facts(p.playerId)} />
                  </span>
                  {enableToggle(p.playerId)}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="card card--sm card--bg-midnight card--border card--border-navy">
        <h3>Joined without a sign-up ({unmatched.length})</h3>
        <p className="field__hint">
          Walk-ins, or someone whose Discord didn't link. If they did sign up, match them to their sign-up: they take
          over its place on the team (and its captaincy). Otherwise put them on a team.
        </p>
        {unmatched.length === 0 ? (
          <p className="text-slate fs-sm">Every player who joined matches a sign-up.</p>
        ) : (
          <div className="d-flex flex-column gap-2xs">
            {unmatched.map((p) => (
              <MatchRow
                key={p.playerId}
                player={p}
                label={name(p.playerId)}
                facts={facts(p.playerId)}
                standIns={standIns}
                standInLabel={(s) => name(s.playerId)}
                disabled={readOnly}
                onMatch={(standIn) => matchToSignup(p.playerId, standIn)}
              />
            ))}
          </div>
        )}
      </div>

      {(canDraft || game.hnstDraft) && <DraftPanel game={game} canDraft={canDraft} run={run} note={(id) => factsNote(facts(id))} />}
    </div>
  );
}

function MatchRow({
  player,
  label,
  facts,
  standIns,
  standInLabel,
  disabled,
  onMatch,
}: {
  player: Player;
  label: string;
  facts: PlayerFacts;
  standIns: Player[];
  standInLabel: (standIn: Player) => string;
  disabled: boolean;
  onMatch: (standIn: Player) => void;
}) {
  const [standInId, setStandInId] = useState('');
  const standIn = standIns.find((s) => s.playerId === standInId);
  return (
    <div className="d-flex align-items-center gap-xs">
      <PlayerAvatar avatarId={player.avatarId} />
      <span className="flex-grow-1">
        {label}
        {player.discord?.discordUsername && <span className="text-slate fs-xs"> (Discord @{player.discord.discordUsername})</span>}
        <PlayerBadges facts={facts} />
      </span>
      {!disabled && standIns.length > 0 && (
        <>
          <select
            className="select"
            aria-label={`Sign-up to match ${label} to`}
            value={standInId}
            onChange={(e) => setStandInId(e.target.value)}
          >
            <option value="">Match to sign-up…</option>
            {standIns.map((s) => <option key={s.playerId} value={s.playerId}>{standInLabel(s)}</option>)}
          </select>
          <button
            className="btn btn--sm btn--outline btn--aqua"
            disabled={!standIn}
            onClick={() => {
              if (!standIn) return;
              onMatch(standIn);
              setStandInId('');
            }}
          >
            Match
          </button>
        </>
      )}
    </div>
  );
}

function DraftPanel({
  game,
  canDraft,
  run,
  note,
}: {
  game: Game;
  canDraft: boolean;
  run: (action: Promise<HnstActionResponse>) => Promise<void>;
  /** A short note after a player's name (e.g. not joined yet). */
  note: (playerId: string) => string;
}) {
  const actions = useHnstContext();
  const draft = game.hnstDraft;
  const running = draft?.status === 'in_progress';
  const [pickSeconds, setPickSeconds] = useState(game.hnstDraftConfig?.timePerPickSeconds ?? HNST_DRAFT_PICK_SECONDS_DEFAULT);
  const [firstPick, setFirstPick] = useState<'' | HnstTeamKey>('');
  const name = (playerId: string) => getPlayerDisplayName(playerId, game.players);

  // The pick countdown ticks while the draft runs, by the server's clock (the
  // deadline is the server's).
  const serverOffsetMs = useServerClockOffset();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [running]);

  const picking = useMemo(() => (running && draft ? getCurrentPicker(draft) : null), [running, draft]);
  const secondsLeft = running && draft ? Math.max(0, Math.ceil((draft.pickDeadline - (now + serverOffsetMs)) / 1000)) : 0;

  return (
    <div className="card card--sm card--bg-midnight card--border card--border-navy">
      <h3>Snake Draft</h3>
      <p className="field__hint">
        The two Team Captains take turns to pick (A B B A A B …) from every player on no team, until nobody is left.
        A captain who runs out of time gets a random pick. Players who signed up can be picked before they join.
      </p>

      {!running && canDraft && (
        <div className="d-flex flex-wrap gap-sm align-items-end">
          <div className="field">
            <label htmlFor="hnst-pick-seconds">Seconds per pick</label>
            <input
              id="hnst-pick-seconds"
              className="input"
              type="number"
              min={HNST_DRAFT_PICK_SECONDS_MIN}
              max={HNST_DRAFT_PICK_SECONDS_MAX}
              value={pickSeconds}
              onChange={(e) => setPickSeconds(Number(e.target.value))}
            />
          </div>
          <button className="btn btn--outline btn--slate" onClick={() => run(actions.configureDraft(pickSeconds))}>
            Save
          </button>
          <div className="field">
            <label htmlFor="hnst-first-pick">First pick</label>
            <select id="hnst-first-pick" className="select" value={firstPick} onChange={(e) => setFirstPick(e.target.value as '' | HnstTeamKey)}>
              <option value="">Random</option>
              {HNST_TEAM_KEYS.map((key) => <option key={key} value={key}>{HNST_TEAMS[key].name}</option>)}
            </select>
          </div>
          <button className="btn btn--green" onClick={() => run(actions.startDraft(firstPick || undefined))}>
            ▶️ Start Draft
          </button>
        </div>
      )}

      {running && draft && picking && (
        <>
          <p className="fs-lg">
            Picking: <HnstTeamName team={picking} /> — <strong>{secondsLeft}s</strong> left
          </p>
          <div className="d-flex flex-wrap gap-xs">
            {draft.pool.map((playerId) => (
              <button key={playerId} className="btn btn--outline btn--aqua" onClick={() => run(actions.draftPick(playerId))} title="Pick for the captain">
                {name(playerId)}{note(playerId)}
              </button>
            ))}
          </div>
          <button className="btn btn--red mt-xs" onClick={() => run(actions.cancelDraft())}>
            Stop Draft
          </button>
        </>
      )}

      {draft && draft.picks.length > 0 && (
        <div className="mt-xs">
          <strong>{running ? 'Picks so far' : 'Draft picks'}</strong>
          <ol className="fs-sm">
            {draft.picks.map((pick) => (
              <li key={pick.pickNumber}>
                <HnstTeamName team={pick.team} />: {name(pick.playerId)}{pick.auto ? ' (out of time — random)' : ''}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
